import { Button, useSnackbars } from '@customerio/pluma-components/react';
import { AiEnabledGuard } from '@/components/guards';
import { useGenerateDataIndexDescriptions } from '@/services/db/data-index/mutations';
import type { DataIndexAnalyticsType } from '../utils/types';

/**
 * Ask the model to describe one row.
 *
 * What comes back is put in the box, not saved: the description is the
 * workspace's, so somebody reads it before it becomes the answer everyone
 * else sees.
 */

const FAILED_MESSAGE = 'Failed to generate description. Try again.';

export type GenerateDescriptionButtonProps = {
  workspaceId: number;
  type: DataIndexAnalyticsType;
  name: string;
  /** Absent for a person attribute or an event, which belong to no object type. */
  objectTypeId?: number;
  isDisabled: boolean;
  onGenerated: (description: string) => void;
};

export function GenerateDescriptionButton(props: GenerateDescriptionButtonProps) {
  return (
    <AiEnabledGuard>
      <GenerateButton {...props} />
    </AiEnabledGuard>
  );
}

function GenerateButton({
  workspaceId,
  type,
  name,
  objectTypeId,
  isDisabled,
  onGenerated,
}: GenerateDescriptionButtonProps) {
  const snackbars = useSnackbars();
  const generate = useGenerateDataIndexDescriptions({ workspaceId });

  const run = async () => {
    try {
      const [generated] = await generate.mutateAsync({ type, items: [name], objectTypeId });
      if (generated == null) {
        throw new Error('no description returned');
      }
      onGenerated(generated.description);
    } catch {
      snackbars.create({ id: `data-index-generate-${name}`, variant: 'critical', message: FAILED_MESSAGE });
    }
  };

  return (
    <Button
      variant="secondary"
      size="sm"
      icon="sparkle"
      isIconOnly
      aria-label="Generate description with AI"
      isDisabled={isDisabled}
      isLoading={generate.isPending}
      onClick={() => void run()}
    />
  );
}
